import React from "react";
import styled from "@emotion/styled/macro";
import ExpandMapBtn from "../../assets/images/expand-map-btn.svg";
import sitePlaceholder from "components/assets/images/site-model-placeholder.png";
import { maxWidth } from "components/atoms/GlobalStyles";
import { DesignRequest } from "providers/Types";
import { isSiteModelComplete } from "utils/siteModelUtils";

type SiteModelImageProps = {
  designRequest: DesignRequest | undefined | null;
  siteModelImageUrl?: string | null;
  siteModelHasRoofPlanes?: boolean;
  isDesignRequestRequired?: boolean;
  isMapModalOpen: boolean;
  setIsMapModalOpen: (arg0: boolean) => void;
  style?: React.CSSProperties;
  mapStyle?: React.CSSProperties;
};

const SiteModelImage: React.FC<SiteModelImageProps> = ({
  designRequest,
  siteModelImageUrl,
  siteModelHasRoofPlanes = false,
  isDesignRequestRequired = true,
  isMapModalOpen,
  setIsMapModalOpen,
  style,
  mapStyle,
}) => {
  const hasImage =
    isSiteModelComplete(
      isDesignRequestRequired,
      designRequest,
      siteModelHasRoofPlanes
    ) && Boolean(siteModelImageUrl);

  const image = hasImage ? (
    <SiteModelImageGraphic
      src={siteModelImageUrl!}
      alt="Site model"
      data-testid="site-model-image"
      style={mapStyle}
    />
  ) : (
    <SiteModelPlaceholderGraphic
      src={sitePlaceholder}
      data-testid="placeholder-image"
    />
  );

  return (
    <SiteImageContainer
      isPlaceHolderImg={!hasImage}
      isMapModalOpen={isMapModalOpen}
      style={style}
    >
      {!isMapModalOpen && hasImage && (
        <ExpandMapButton onClick={() => setIsMapModalOpen(true)}>
          <img src={ExpandMapBtn} alt="Expand Site Model Map Button" />
        </ExpandMapButton>
      )}
      {image}
    </SiteImageContainer>
  );
};

const SiteImageContainer = styled.div<{
  isPlaceHolderImg: boolean;
  isMapModalOpen: boolean;
}>`
  position: relative;
  flex: 1 1 30%;
  padding: 8px;
  min-height: ${({ isPlaceHolderImg, isMapModalOpen }) =>
    isPlaceHolderImg ? "auto" : isMapModalOpen ? "32rem" : "24rem"};
  margin: ${({ isPlaceHolderImg }) => (isPlaceHolderImg ? "auto" : "initial")};
  margin-bottom: ${({ isPlaceHolderImg }) =>
    isPlaceHolderImg ? "0px" : "24px"};
`;

const ExpandMapButton = styled.button`
  z-index: 50;
  position: absolute;
  right: 20px;
  top: 20px;

  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  outline: inherit;

  &:hover {
    opacity: 0.7;
  }
`;

const SiteModelImageGraphic = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
`;

const SiteModelPlaceholderGraphic = styled.img`
  max-width: 100%;
  min-width: 140px;
  min-height: 140px;
  max-height: 450px;
  border-radius: 16px;
  @media (max-width: ${maxWidth}px) {
    object-fit: cover;
    aspect-ratio: 2/1;
  }
`;

export { SiteModelImage };
export type { SiteModelImageProps };
